/**
 * The Squares setup step's draft, its "what is still missing" list, and the
 * `squaresSetup` payload `/pool/create` carries (ISSUE-1258, ISSUE-1269).
 *
 * `features/createPool` calls the three exported contract functions through
 * `poolTypeSetups.ts`. Server resolves, client renders: league presets, game
 * previews and defaults come from `useSquaresPresetsGet` and
 * `useSquaresPreviewGames`, never from a table here. The only facts the draft
 * keeps from the preview are the ones the server already decided: how many
 * games are included, whether Create is allowed, and whether the count needs a
 * yes first.
 */
import type { usePoolCreate } from "@sportsmet/pool-sdk/private";

import { squaresLargeCountMissingItem } from "./squaresCopy";

import type {
  typeSquaresAxesKind,
  typeSquaresGameRule,
  typeSquaresGridType,
  typeSquaresPresetKey,
  typeSquaresPresetsData,
} from "./squaresTypes";

type typePoolCreateRequest = Parameters<
  ReturnType<typeof usePoolCreate>["mutateAsync"]
>[0];

/** Exactly the `squaresSetup` object `/pool/create` accepts. */
export type typeSquaresSetupPayload = NonNullable<
  typePoolCreateRequest["squaresSetup"]
>;

/** What the server's live preview decided for the current game rule. */
export type typeSquaresPreviewFacts = {
  includedCount: number;
  canCreate: boolean;
  needsConfirm: boolean;
};

export type typeSquaresSetupDraft = {
  /**
   * The league this draft was built for. A league change starts over. Absent
   * on a draft built outside the setup step.
   */
  leagueKey?: string | null | undefined;
  gridType: typeSquaresSetupPayload["gridType"] | null;
  gameRule: typeSquaresSetupPayload["gameRule"] | null;
  axes: typeSquaresSetupPayload["axes"] | null;
  checkpoints: readonly string[];
  gridSettings: typeSquaresSetupPayload["gridSettings"] | null;
  newGrid: typeSquaresSetupPayload["newGrid"];
  /**
   * Null while the server's preview for the current rule is pending. Only the
   * setup step writes it; a draft built anywhere else leaves it absent, and
   * then the bootstrap's own `validateSquaresSetup` is the only check.
   */
  preview?: typeSquaresPreviewFacts | null | undefined;
  isLargeCountConfirmed?: boolean | undefined;
};

export const EMPTY_SQUARES_SETUP_DRAFT: typeSquaresSetupDraft = {
  leagueKey: null,
  gridType: null,
  gameRule: null,
  axes: null,
  checkpoints: ["final"],
  gridSettings: null,
  newGrid: { carrySquares: false, redrawNumbers: true, autoPublish: false },
  preview: null,
  isLargeCountConfirmed: false,
};

/** A fresh draft for a league, keeping nothing from another league. */
export function startSquaresSetupDraft(
  leagueKey: string | null,
): typeSquaresSetupDraft {
  return { ...EMPTY_SQUARES_SETUP_DRAFT, leagueKey };
}

function gameRuleMissingItem(gameRule: typeSquaresGameRule): string | null {
  if (gameRule.presetKey === "oneGame") {
    return gameRule.eventId === null || gameRule.eventId === undefined
      ? "the game"
      : null;
  }
  if (gameRule.presetKey === "customDates") {
    const dates = gameRule.customDates;
    if (
      dates === null ||
      dates === undefined ||
      (dates.fromDay ?? "") === "" ||
      (dates.toDay ?? "") === ""
    ) {
      return "the dates";
    }
    return null;
  }
  if (gameRule.presetKey === "team") {
    return gameRule.teamId === null || gameRule.teamId === undefined
      ? "a team"
      : null;
  }
  if (gameRule.presetKey === "conference") {
    return gameRule.conferenceKey === null ||
      gameRule.conferenceKey === undefined
      ? "a conference"
      : null;
  }
  return null;
}

/** True once the rule names everything the server needs to preview it. */
export function isSquaresGameRuleReady(gameRule: typeSquaresGameRule): boolean {
  return gameRuleMissingItem(gameRule) === null;
}

/** Plain-English items the create footer lists while Create is disabled. */
export function squaresSetupMissingItems(
  draft: typeSquaresSetupDraft,
): readonly string[] {
  const missing: string[] = [];
  if (draft.gridType === null) missing.push("a grid type");
  if (draft.gameRule === null) {
    missing.push("the games");
  } else {
    const ruleGap = gameRuleMissingItem(draft.gameRule);
    if (ruleGap !== null) {
      missing.push(ruleGap);
    } else if (draft.preview === null) {
      missing.push("the games");
    } else if (draft.preview === undefined) {
      // Built outside the setup step: nothing to gate on here.
    } else if (!draft.preview.canCreate) {
      missing.push("a game on the schedule");
    } else if (
      draft.preview.needsConfirm &&
      draft.isLargeCountConfirmed !== true
    ) {
      missing.push(
        squaresLargeCountMissingItem({
          gridType: draft.gridType ?? "classic",
          count: draft.preview.includedCount,
        }),
      );
    }
  }
  if (draft.axes === null) {
    missing.push("the axes");
  } else if (
    draft.axes.kind === "custom" &&
    ((draft.axes.topLabel ?? "").trim() === "" ||
      (draft.axes.sideLabel ?? "").trim() === "")
  ) {
    missing.push("both axis labels");
  }
  if (draft.gridSettings === null) missing.push("the grid settings");
  return missing;
}

/**
 * How many grids Create makes right now: one per included game for Classic,
 * always one for Shared, and zero while the games are not settled. The same
 * rule as the phone's `squaresSetupGridCount`.
 */
export function squaresSetupGridCount(draft: typeSquaresSetupDraft): number {
  if (draft.gridType === null) return 0;
  if (draft.gridType === "shared") return 1;
  const rule = draft.gameRule;
  if (rule?.presetKey === "oneGame") {
    return rule.eventId === null || rule.eventId === undefined ? 0 : 1;
  }
  return draft.preview?.includedCount ?? 0;
}

/** The `squaresSetup` payload, or null while anything is missing. */
export function buildSquaresSetupPayload(
  draft: typeSquaresSetupDraft,
): typeSquaresSetupPayload | null {
  if (squaresSetupMissingItems(draft).length > 0) return null;
  if (
    draft.gridType === null ||
    draft.gameRule === null ||
    draft.axes === null ||
    draft.gridSettings === null
  ) {
    return null;
  }
  const axes =
    draft.axes.kind === "custom"
      ? {
          ...draft.axes,
          topLabel: (draft.axes.topLabel ?? "").trim(),
          sideLabel: (draft.axes.sideLabel ?? "").trim(),
        }
      : draft.axes;
  return {
    gridType: draft.gridType,
    gameRule: draft.gameRule,
    axes,
    checkpoints: [...draft.checkpoints],
    gridSettings: draft.gridSettings,
    newGrid: draft.newGrid,
  };
}

function isPresetForType(
  presets: typeSquaresPresetsData,
  presetKey: string,
  gridType: typeSquaresGridType,
): boolean {
  return presets.gamePresets.some(
    (preset) =>
      preset.presetKey === presetKey && preset.gridTypes.includes(gridType),
  );
}

function firstAxesKindForType(
  presets: typeSquaresPresetsData,
  gridType: typeSquaresGridType,
): typeSquaresAxesKind | null {
  return (
    presets.axesPresets.find(
      (preset) =>
        preset.kind !== "custom" && preset.gridTypes.includes(gridType),
    )?.kind ?? null
  );
}

/**
 * Picks Classic or Shared. A preset or axes choice the new type does not offer
 * is dropped; the league's own default grid size fills the settings the first
 * time. Final is on from the start.
 */
export function chooseSquaresGridType(input: {
  draft: typeSquaresSetupDraft;
  gridType: typeSquaresGridType;
  presets: typeSquaresPresetsData;
}): typeSquaresSetupDraft {
  const { draft, gridType, presets } = input;
  const keepsRule =
    draft.gameRule !== null &&
    isPresetForType(presets, draft.gameRule.presetKey, gridType);
  const keepsAxes =
    draft.axes !== null &&
    presets.axesPresets.some(
      (preset) =>
        preset.kind === draft.axes?.kind && preset.gridTypes.includes(gridType),
    );
  const firstKind = firstAxesKindForType(presets, gridType);
  const alwaysOn = presets.checkpoints
    .filter((checkpoint) => checkpoint.isAlwaysOn)
    .map((checkpoint) => checkpoint.checkpointKey as string);
  const checkpoints = [...new Set([...alwaysOn, ...draft.checkpoints])];
  return {
    ...draft,
    gridType,
    gameRule: keepsRule ? draft.gameRule : null,
    axes: keepsAxes
      ? draft.axes
      : firstKind === null
        ? null
        : { kind: firstKind },
    checkpoints,
    gridSettings: draft.gridSettings ?? {
      size: presets.defaultGridSize,
      maxPerMember: null,
      autoLock: true,
      showNumbers: true,
    },
    preview: keepsRule ? draft.preview : null,
    isLargeCountConfirmed: keepsRule ? draft.isLargeCountConfirmed : false,
  };
}

/** A new preset starts its rule over: no game, no dates, nothing excluded. */
export function chooseSquaresPreset(input: {
  draft: typeSquaresSetupDraft;
  presetKey: typeSquaresPresetKey;
}): typeSquaresSetupDraft {
  const teamId =
    input.presetKey === "oneGame" || input.presetKey === "customDates"
      ? null
      : (input.draft.gameRule?.teamId ?? null);
  return setSquaresGameRule({
    draft: input.draft,
    gameRule: {
      presetKey: input.presetKey,
      eventId: null,
      teamId,
      customDates: null,
      excludedEventIds: [],
    },
  });
}

/** Any change to the rule drops the old preview until the server answers again. */
export function setSquaresGameRule(input: {
  draft: typeSquaresSetupDraft;
  gameRule: typeSquaresGameRule;
}): typeSquaresSetupDraft {
  const previousTeam = input.draft.gameRule?.teamId ?? null;
  const nextTeam = input.gameRule.teamId ?? null;
  let axes = input.draft.axes;
  if (nextTeam !== null && nextTeam !== previousTeam) {
    // Choosing a team puts that team across the top and Opponent down the side.
    axes = { kind: "teamOpponent", teamId: nextTeam };
  } else if (
    nextTeam === null &&
    previousTeam !== null &&
    axes?.kind === "teamOpponent"
  ) {
    axes = { kind: "teamOpponent" };
  }
  return {
    ...input.draft,
    gameRule: input.gameRule,
    axes,
    preview: null,
    isLargeCountConfirmed: false,
  };
}

export function toggleSquaresExcludedEvent(input: {
  draft: typeSquaresSetupDraft;
  eventId: string;
}): typeSquaresSetupDraft {
  const rule = input.draft.gameRule;
  if (rule === null) return input.draft;
  const excluded = new Set(rule.excludedEventIds);
  if (excluded.has(input.eventId)) excluded.delete(input.eventId);
  else excluded.add(input.eventId);
  return setSquaresGameRule({
    draft: input.draft,
    gameRule: { ...rule, excludedEventIds: [...excluded] },
  });
}

/** Writes the server's preview answer, or returns the same draft if nothing changed. */
export function applySquaresPreviewFacts(input: {
  draft: typeSquaresSetupDraft;
  facts: typeSquaresPreviewFacts;
}): typeSquaresSetupDraft {
  const current = input.draft.preview ?? null;
  if (
    current !== null &&
    current.includedCount === input.facts.includedCount &&
    current.canCreate === input.facts.canCreate &&
    current.needsConfirm === input.facts.needsConfirm
  ) {
    return input.draft;
  }
  return {
    ...input.draft,
    preview: input.facts,
    isLargeCountConfirmed: input.facts.needsConfirm
      ? (input.draft.isLargeCountConfirmed ?? false)
      : false,
  };
}

/** Final (and any other always-on checkpoint) can never be switched off. */
export function setSquaresCheckpoints(input: {
  draft: typeSquaresSetupDraft;
  checkpoints: readonly string[];
  alwaysOn: readonly string[];
}): typeSquaresSetupDraft {
  return {
    ...input.draft,
    checkpoints: [...new Set([...input.alwaysOn, ...input.checkpoints])],
  };
}
